// third-party
import { createSlice } from '@reduxjs/toolkit';

// project imports
import axios from 'utils/axios';
import { dispatch } from '../index';

// ----------------------------------------------------------------------

const initialState = {
    error: null,
    events: [],
    eventItem: null,
    loading: false,
    pagination: {
        total: 0,
        currentPage: 1,
        totalPages: 0,
        limit: 20
    }
};

const slice = createSlice({
    name: 'events',
    initialState,
    reducers: {
        // HAS ERROR
        hasError(state, action) {
            state.error = action.payload;
            state.loading = false;
        },

        // SET LOADING
        setLoading(state, action) {
            state.loading = action.payload;
        },

        // CLEAR ERROR
        clearError(state) {
            state.error = null;
        },

        // GET events
        getEventsSuccess(state, action) {
            state.events = action.payload.data;
            state.pagination = action.payload.pagination;
            state.loading = false;
            state.error = null;
        },

        // CREATE event
        createEventSuccess(state, action) {
            state.events.unshift(action.payload);
            state.pagination.total += 1;
            state.loading = false;
            state.error = null;
        },

        // UPDATE event
        updateEventSuccess(state, action) {
            const index = state.events.findIndex(item => item.id === action.payload.id);
            if (index !== -1) {
                state.events[index] = action.payload;
            }
            state.loading = false;
            state.error = null;
        },

        // DELETE event
        deleteEventSuccess(state, action) {
            state.events = state.events.filter(item => item.id !== action.payload);
            state.pagination.total -= 1;
            state.loading = false;
            state.error = null;
        }
    }
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export function fetchEvents(page = 1, limit = 20, filters = {}) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            
            let queryParams = `page=${page}&limit=${limit}`;
            
            // Add filters to query params
            Object.keys(filters).forEach(key => {
                if (filters[key]) {
                    queryParams += `&${key}=${encodeURIComponent(filters[key])}`;
                }
            });
            
            const response = await axios.get(`/api/rooms/events?${queryParams}`);
            dispatch(slice.actions.getEventsSuccess(response.data));
        } catch (error) {
            dispatch(slice.actions.hasError(error.response?.data?.message || 'Erreur lors de la récupération des événements'));
        }
    };
}

export function createEvent(data) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await axios.post('/api/rooms/events', data);
            dispatch(slice.actions.createEventSuccess(response.data.data));
            return { success: true, data: response.data.data };
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Erreur lors de la création de l\'événement';
            dispatch(slice.actions.hasError(errorMessage));
            return { success: false, error: errorMessage };
        }
    };
}

export function updateEvent(id, data) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await axios.put(`/api/rooms/events/${id}`, data);
            dispatch(slice.actions.updateEventSuccess(response.data.data));
            return { success: true, data: response.data.data };
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Erreur lors de la modification de l\'événement';
            dispatch(slice.actions.hasError(errorMessage));
            return { success: false, error: errorMessage };
        }
    };
}

export function deleteEvent(id) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            await axios.delete(`/api/rooms/events/${id}`);
            dispatch(slice.actions.deleteEventSuccess(id));
            return { success: true };
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Erreur lors de la suppression de l\'événement';
            dispatch(slice.actions.hasError(errorMessage));
            return { success: false, error: errorMessage };
        }
    };
}

export function cancelEvent(id) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await axios.patch(`/api/rooms/events/${id}/cancel`);
            dispatch(slice.actions.updateEventSuccess(response.data.data));
            return { success: true, data: response.data.data };
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Erreur lors de l\'annulation de l\'événement';
            dispatch(slice.actions.hasError(errorMessage));
            return { success: false, error: errorMessage };
        }
    };
}

export function draftEvent(id) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await axios.patch(`/api/rooms/events/${id}/draft`);
            dispatch(slice.actions.updateEventSuccess(response.data.data));
            return { success: true, data: response.data.data };
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Erreur lors de la mise en brouillon de l\'événement';
            dispatch(slice.actions.hasError(errorMessage));
            return { success: false, error: errorMessage };
        }
    };
}

// Clear error action
export const clearEventsError = () => dispatch(slice.actions.clearError());
